import { translate, type WashedLocale } from '@washed/i18n';
import { PageShell } from './PageShell.js';
import styles from './SeoIntentPage.module.css';

const SITE = 'https://washedafrica.com';

export type SeoExpansionKey =
  | 'family-laundry'
  | 'busy-laundry'
  | 'someone-to-wash'
  | 'washerwoman-vs-housekeeper'
  | 'pay-family-laundry'
  | 'washerwoman-jobs';

type Section = { heading: string; paragraphs: string[] };
type Faq = { q: string; a: string };
type Related = { href: string; label: string };
type IntentCopy = {
  slug: string;
  eyebrow: string;
  title: string;
  intro: string;
  serviceType?: string;
  sections: Section[];
  faqs: Faq[];
  related: Related[];
  action: { href: string; label: string; detail: string };
};

const PATH_TO_INTENT: Record<string, SeoExpansionKey> = {
  '/lessive-famille-lome': 'family-laundry',
  '/family-laundry-service-lome': 'family-laundry',
  '/pas-le-temps-lessive-lome': 'busy-laundry',
  '/laundry-for-busy-people-lome': 'busy-laundry',
  '/qui-peut-laver-mon-linge-lome': 'someone-to-wash',
  '/someone-to-wash-my-clothes-lome': 'someone-to-wash',
  '/laveuse-ou-aide-menagere': 'washerwoman-vs-housekeeper',
  '/washerwoman-vs-housekeeper-lome': 'washerwoman-vs-housekeeper',
  '/payer-lessive-proche-togo': 'pay-family-laundry',
  '/pay-for-family-laundry-togo': 'pay-family-laundry',
  '/travail-laveuse-lome': 'washerwoman-jobs',
  '/washerwoman-jobs-lome': 'washerwoman-jobs',
};

export function seoExpansionKeyFromPath(path: string): SeoExpansionKey | null {
  const normalized = path.replace(/\/+$/u, '') || '/';
  return PATH_TO_INTENT[normalized] ?? null;
}

const COPY: Record<SeoExpansionKey, Record<WashedLocale, IntentCopy>> = {
  'family-laundry': {
    fr: {
      slug: 'lessive-famille-lome',
      eyebrow: 'Washed · Familles',
      title: 'Service de lessive pour les familles à Lomé',
      intro:
        'Quand plusieurs personnes partagent le même foyer, le linge devient une tâche récurrente à organiser. Washed prévoit des formules Famille et des visites à domicile pour déléguer cette routine sans envoyer les vêtements ailleurs.',
      serviceType: 'Service de lessive à domicile pour familles à Lomé',
      sections: [
        {
          heading: 'Un service pensé pour un foyer, pas seulement pour une personne',
          paragraphs: [
            'Les formules Famille de Washed sont destinées aux foyers de deux personnes ou plus. La laveuse vient au domicile et lave sur place avec l’eau et le savon du foyer.',
            'Le fonctionnement reste récurrent : un planning organisé, une laveuse habituelle réassignée par défaut et un remplacement possible lorsqu’un changement est nécessaire.',
          ],
        },
        {
          heading: 'Quel budget prévoir ?',
          paragraphs: [
            'Les formules pour plusieurs personnes sont actuellement Duo à 6 000 / 11 500 XOF par mois et Foyer à 9 500 / 18 000 XOF. Le bon choix dépend de la taille du foyer et du rythme de visites.',
            'Chaque visite est prévue pour la quantité raisonnable de linge du foyer enregistré correspondant à la période de service. Un volume clairement excessif est signalé pour examen.',
          ],
        },
      ],
      faqs: [
        {
          q: 'À partir de combien de personnes parle-t-on de formule Famille ?',
          a: 'Duo et Foyer sont prévues pour deux personnes ou plus qui font laver leur linge à la même adresse : une famille, mais aussi des colocataires ou des amis.',
        },
        {
          q: 'La laveuse vient-elle chez nous ?',
          a: 'Oui. La lessive est faite au domicile ; le linge n’est pas emporté.',
        },
        {
          q: 'Quelles formules existent pour plusieurs personnes ?',
          a: 'Quatre formules : Duo, pour 2 personnes, à 6 000 XOF par mois pour une visite ou 11 500 XOF pour deux ; Foyer, pour 3 à 5 personnes à la même adresse (famille, colocataires ou amis), à 9 500 XOF pour une visite ou 18 000 XOF pour deux.',
        },
      ],
      related: [
        { href: '/prix-laveuse-lome', label: 'Voir les tarifs Washed' },
        { href: '/abonnement-lessive-lome', label: 'Comprendre l’abonnement lessive' },
        { href: '/trouver-laveuse-fiable-lome', label: 'Trouver une laveuse fiable' },
      ],
      action: {
        href: '/#rejoindre',
        label: 'Rejoindre la liste d’attente',
        detail: 'Indiquez votre quartier à Lomé et votre besoin familial.',
      },
    },
    en: {
      slug: 'family-laundry-service-lome',
      eyebrow: 'Washed · Families',
      title: 'Family laundry service in Lomé',
      intro:
        'When several people share a household, laundry becomes a recurring coordination problem. Washed offers family plans and in-home visits so households can delegate the routine without sending clothes away.',
      serviceType: 'In-home family laundry service in Lomé',
      sections: [
        {
          heading: 'Built for a household, not only one person',
          paragraphs: [
            'Washed family plans are intended for households of two or more people. The washerwoman comes to the home and washes on-site using the household’s water and soap.',
            'The model stays recurring: organized scheduling, the regular washerwoman reassigned by default and a replacement available when a change is needed.',
          ],
        },
        {
          heading: 'What budget should a family expect?',
          paragraphs: [
            'Washed currently publishes Duo at 6,000 / 11,500 XOF per month and Household at 9,500 / 18,000 XOF. The right plan depends on household size and visit frequency.',
            'Each visit is designed for a reasonable amount of laundry for the registered household and service period. Clearly excessive volume is flagged for review.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Who qualifies for a family plan?',
          a: 'Duo and Household are for two or more people whose laundry is washed at one address: a family, but also flatmates or friends.',
        },
        {
          q: 'Does the washerwoman come to our home?',
          a: 'Yes. Laundry is washed at the home and is not taken away.',
        },
        {
          q: 'Which plans cover more than one person?',
          a: 'Four plans: Duo, for 2 people, at 6,000 XOF per month for one visit or 11,500 XOF for two; Household, for 3 to 5 people at one address (family, flatmates or friends), at 9,500 XOF for one visit or 18,000 XOF for two.',
        },
      ],
      related: [
        { href: '/en/laundry-prices-lome', label: 'See Washed pricing' },
        { href: '/en/laundry-subscription-lome', label: 'Understand the monthly subscription' },
        { href: '/en/reliable-washerwoman-lome', label: 'Find a reliable washerwoman' },
      ],
      action: {
        href: '/en#rejoindre',
        label: 'Join the waitlist',
        detail: 'Tell us your Lomé neighbourhood and household need.',
      },
    },
  },
  'busy-laundry': {
    fr: {
      slug: 'pas-le-temps-lessive-lome',
      eyebrow: 'Washed · Gain de temps',
      title: 'Pas le temps de faire la lessive à Lomé ?',
      intro:
        'Si le problème n’est pas de savoir laver mais de trouver le temps, Washed transforme la lessive en rendez-vous récurrent : une laveuse vient chez vous une ou deux fois par mois et s’occupe du linge sur place.',
      serviceType: 'Service de lessive pour personnes occupées à Lomé',
      sections: [
        {
          heading: 'Déléguer uniquement la tâche qui prend du temps',
          paragraphs: [
            'Vous n’avez pas besoin d’engager une aide ménagère pour toutes les tâches de la maison si votre principal problème est le linge. Washed est centré sur la lessive à domicile.',
            'Le foyer fournit l’eau et le savon ; Washed organise la laveuse, le planning et le paiement.',
          ],
        },
        {
          heading: 'Réduire la coordination autant que le travail manuel',
          paragraphs: [
            'Chercher quelqu’un à chaque fois, confirmer sa disponibilité et renégocier une mission prend aussi du temps. L’abonnement Washed vise à réduire cette coordination avec des visites planifiées et une relation régulière.',
            'Les formules Solo commencent à 4 500 XOF par mois pour une visite et 8 500 XOF pour deux visites.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Washed fait-il d’autres tâches ménagères ?',
          a: 'Le service Washed présenté ici est centré sur la lessive à domicile, pas sur l’ensemble des tâches ménagères.',
        },
        {
          q: 'Combien de visites sont possibles ?',
          a: 'Les formules individuelles actuelles prévoient une ou deux visites par mois.',
        },
        {
          q: 'Dois-je être présent pendant toute la lessive ?',
          a: 'Washed organise une prestation au domicile ; les modalités pratiques exactes dépendent du rendez-vous et des règles du service.',
        },
      ],
      related: [
        { href: '/laveuse-a-domicile-lome', label: 'Comment fonctionne une laveuse à domicile' },
        { href: '/abonnement-lessive-lome', label: 'Voir le modèle par abonnement' },
        { href: '/laveuse-ou-aide-menagere', label: 'Laveuse ou aide ménagère ?' },
      ],
      action: {
        href: '/#rejoindre',
        label: 'M’inscrire',
        detail: translate('marketing.seo.coverage.short', 'fr'),
      },
    },
    en: {
      slug: 'laundry-for-busy-people-lome',
      eyebrow: 'Washed · Save time',
      title: 'No time to do laundry in Lomé?',
      intro:
        'If the problem is not knowing how to wash clothes but finding the time, Washed turns laundry into a recurring appointment: a washerwoman comes to your home once or twice a month and handles the washing on-site.',
      serviceType: 'Laundry service for busy people in Lomé',
      sections: [
        {
          heading: 'Delegate the time-consuming task without hiring for everything',
          paragraphs: [
            'You do not need full domestic help if laundry is the main household task you want to delegate. Washed is focused on in-home laundry.',
            'The household provides water and soap; Washed organizes the washerwoman, schedule and payment.',
          ],
        },
        {
          heading: 'Reduce coordination as well as manual work',
          paragraphs: [
            'Finding someone, confirming availability and renegotiating every visit also takes time. A Washed subscription is designed to reduce that coordination through planned visits and a recurring relationship.',
            'Solo plans start at 4,500 XOF per month for one visit and 8,500 XOF for two visits.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Does Washed do other household chores?',
          a: 'The Washed service described here is focused on in-home laundry rather than every domestic task.',
        },
        {
          q: 'How many visits can I book?',
          a: 'Current individual plans provide one or two visits per month.',
        },
        {
          q: 'Do I have to be home for the entire wash?',
          a: 'Washed organizes an in-home service; exact visit logistics depend on the appointment and service rules.',
        },
      ],
      related: [
        { href: '/en/washerwoman-service-lome', label: 'How an in-home washerwoman works' },
        { href: '/en/laundry-subscription-lome', label: 'See the subscription model' },
        { href: '/en/washerwoman-vs-housekeeper-lome', label: 'Washerwoman or housekeeper?' },
      ],
      action: {
        href: '/en#rejoindre',
        label: 'Sign up',
        detail: translate('marketing.seo.coverage.short', 'en'),
      },
    },
  },
  'someone-to-wash': {
    fr: {
      slug: 'qui-peut-laver-mon-linge-lome',
      eyebrow: 'Washed · Besoin concret',
      title: 'Qui peut laver mon linge à Lomé ?',
      intro:
        'Si vous cherchez simplement quelqu’un de fiable pour venir laver vos vêtements, Washed organise une laveuse formée et vérifiée qui intervient directement chez vous.',
      serviceType: 'Personne pour laver le linge à domicile à Lomé',
      sections: [
        {
          heading: 'Une réponse à la recherche informelle de « quelqu’un »',
          paragraphs: [
            'Le bouche-à-oreille fonctionne, mais il laisse souvent au foyer la vérification, la négociation, le planning et le remplacement en cas d’absence. Washed transforme ces étapes en service organisé.',
            'La laveuse intervient à domicile et le linge reste chez vous pendant la prestation.',
          ],
        },
        {
          heading: 'Ce qu’il faut demander avant de choisir',
          paragraphs: [
            'Vérifiez qui vient, comment les rendez-vous sont planifiés, qui gère le paiement, ce qui se passe en cas d’indisponibilité et si vous pouvez garder la même personne dans le temps.',
            'Washed est construit précisément autour de ces questions : laveuses formées et vérifiées, visites planifiées et laveuse habituelle réassignée par défaut.',
          ],
        },
      ],
      faqs: [
        {
          q: 'La personne vient-elle à domicile ?',
          a: 'Oui. Avec Washed, la laveuse vient chez vous et le linge est lavé sur place.',
        },
        {
          q: 'Comment savoir si la laveuse est fiable ?',
          a: 'Washed présente ses laveuses comme formées et vérifiées et organise la relation plutôt que de laisser le foyer gérer seul.',
        },
        {
          q: 'Puis-je demander une personne régulière ?',
          a: 'La laveuse habituelle est réassignée par défaut, avec remplacement lorsque cela est nécessaire.',
        },
      ],
      related: [
        { href: '/trouver-laveuse-fiable-lome', label: 'Les critères d’une laveuse fiable' },
        { href: '/laveuse-a-domicile-lome', label: 'Voir le service de laveuse à domicile' },
        { href: '/prix-laveuse-lome', label: 'Voir les prix' },
      ],
      action: {
        href: '/#rejoindre',
        label: 'Indiquer mon quartier',
        detail: translate('marketing.seo.coverage.short', 'fr'),
      },
    },
    en: {
      slug: 'someone-to-wash-my-clothes-lome',
      eyebrow: 'Washed · Practical need',
      title: 'Who can wash my clothes in Lomé?',
      intro:
        'If you simply need a reliable person to come and wash your clothes, Washed organizes a trained and vetted washerwoman who works directly in your home.',
      serviceType: 'Someone to wash clothes at home in Lomé',
      sections: [
        {
          heading: 'An organized alternative to searching informally',
          paragraphs: [
            'Word of mouth can work, but it often leaves the household to handle vetting, negotiation, scheduling and replacement when someone is absent. Washed turns those steps into an organized service.',
            'The washerwoman works at the home and the laundry stays with you throughout the visit.',
          ],
        },
        {
          heading: 'What to ask before choosing someone',
          paragraphs: [
            'Check who is coming, how appointments are scheduled, who manages payment, what happens when the worker is unavailable and whether you can keep the same person over time.',
            'Washed is built around those questions: trained and vetted washerwomen, planned visits and the regular washerwoman reassigned by default.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Does the person come to my home?',
          a: 'Yes. With Washed, the washerwoman comes to your home and laundry is washed on-site.',
        },
        {
          q: 'How do I know the washerwoman is reliable?',
          a: 'Washed describes its washerwomen as trained and vetted and organizes the relationship instead of leaving households to manage it alone.',
        },
        {
          q: 'Can I keep the same person?',
          a: 'The regular washerwoman is reassigned by default, with a replacement arranged when necessary.',
        },
      ],
      related: [
        { href: '/en/reliable-washerwoman-lome', label: 'How to choose a reliable washerwoman' },
        { href: '/en/washerwoman-service-lome', label: 'See the in-home washerwoman service' },
        { href: '/en/laundry-prices-lome', label: 'See pricing' },
      ],
      action: {
        href: '/en#rejoindre',
        label: 'Tell us your neighbourhood',
        detail: translate('marketing.seo.coverage.short', 'en'),
      },
    },
  },
  'washerwoman-vs-housekeeper': {
    fr: {
      slug: 'laveuse-ou-aide-menagere',
      eyebrow: 'Washed · Comparatif',
      title: 'Laveuse ou aide ménagère à Lomé : quelle différence ?',
      intro:
        'Une aide ménagère peut prendre en charge plusieurs tâches domestiques. Une laveuse Washed est centrée sur une tâche précise : le linge. Le meilleur choix dépend donc de ce que vous voulez réellement déléguer.',
      serviceType: 'Alternative spécialisée à l’aide ménagère pour le linge à Lomé',
      sections: [
        {
          heading: 'Choisir selon le périmètre de travail',
          paragraphs: [
            'Si vous avez besoin de ménage, cuisine, rangement et linge dans un même poste, une aide ménagère correspond davantage à ce périmètre large. Si votre problème principal est la lessive, un service spécialisé évite de payer ou d’organiser des tâches dont vous n’avez pas besoin.',
            'Washed ne présente donc pas la laveuse comme un remplacement universel de l’aide ménagère ; le service est volontairement plus étroit.',
          ],
        },
        {
          heading: 'Comparer organisation, fréquence et relation',
          paragraphs: [
            'Avec Washed, les visites de lessive sont planifiées, le paiement passe par la plateforme et la laveuse habituelle est réassignée par défaut. Le foyer fournit l’eau et le savon.',
            'Cette structure convient aux personnes qui veulent externaliser le linge de façon récurrente tout en gardant les autres tâches domestiques séparées.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Une laveuse Washed fait-elle le ménage ?',
          a: 'Non. Le service est centré sur la lessive à domicile.',
        },
        {
          q: 'Quand choisir une aide ménagère ?',
          a: 'Lorsqu’un foyer veut déléguer un ensemble plus large de tâches domestiques, une aide ménagère peut être plus adaptée.',
        },
        {
          q: 'Quand choisir Washed ?',
          a: 'Lorsque le besoin principal est une lessive régulière à domicile avec planning, paiement et relation organisés.',
        },
      ],
      related: [
        { href: '/lessive-a-domicile-lome', label: 'Voir la lessive à domicile' },
        {
          href: '/pas-le-temps-lessive-lome',
          label: 'Déléguer la lessive quand on manque de temps',
        },
        { href: '/pressing-ou-laveuse', label: 'Pressing ou laveuse ?' },
      ],
      action: {
        href: '/#rejoindre',
        label: 'Voir si Washed correspond à mon besoin',
        detail: 'Inscrivez votre foyer sur la liste d’attente.',
      },
    },
    en: {
      slug: 'washerwoman-vs-housekeeper-lome',
      eyebrow: 'Washed · Comparison',
      title: 'Washerwoman or housekeeper in Lomé: what is the difference?',
      intro:
        'A housekeeper can cover several domestic tasks. A Washed washerwoman focuses on one specific job: laundry. The better choice depends on what you actually want to delegate.',
      serviceType: 'Specialized laundry alternative to general housekeeping in Lomé',
      sections: [
        {
          heading: 'Choose based on the scope of work',
          paragraphs: [
            'If you need cleaning, cooking, tidying and laundry in one role, a housekeeper fits that broader scope. If laundry is the main problem, a specialized service avoids organizing work you do not need.',
            'Washed therefore does not position a washerwoman as a universal replacement for a housekeeper; the service is intentionally narrower.',
          ],
        },
        {
          heading: 'Compare organization, frequency and continuity',
          paragraphs: [
            'With Washed, laundry visits are scheduled, payment runs through the platform and the regular washerwoman is reassigned by default. The household provides water and soap.',
            'That structure suits people who want recurring laundry help while keeping other domestic tasks separate.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Does a Washed washerwoman clean the house?',
          a: 'No. The service is focused on in-home laundry.',
        },
        {
          q: 'When should I choose a housekeeper?',
          a: 'When the household wants to delegate a broader set of domestic tasks, a housekeeper may be a better fit.',
        },
        {
          q: 'When should I choose Washed?',
          a: 'When the main need is recurring in-home laundry with organized scheduling, payment and worker continuity.',
        },
      ],
      related: [
        { href: '/en/in-home-laundry-lome', label: 'See in-home laundry' },
        { href: '/en/laundry-for-busy-people-lome', label: 'Delegate laundry when you are busy' },
        {
          href: '/en/dry-cleaning-vs-home-laundry-lome',
          label: 'Dry cleaning or in-home laundry?',
        },
      ],
      action: {
        href: '/en#rejoindre',
        label: 'See if Washed fits your need',
        detail: 'Add your household to the waitlist.',
      },
    },
  },
  'pay-family-laundry': {
    fr: {
      slug: 'payer-lessive-proche-togo',
      eyebrow: 'Washed · PayFor',
      title: 'Payer la lessive d’un proche au Togo depuis l’étranger',
      intro:
        'PayFor permet à un proche de prendre en charge un abonnement Washed pour un foyer à Lomé. Le paiement se fait à partir d’un lien d’invitation sécurisé ; le bénéficiaire garde son service local et le payeur n’accède pas aux détails privés du foyer.',
      serviceType: 'Paiement d’un abonnement de lessive pour un proche à Lomé',
      sections: [
        {
          heading: 'Comment fonctionne PayFor',
          paragraphs: [
            'Le parcours de paiement Washed utilise un lien PayFor contenant une invitation valide. Le payeur voit le montant du plan, choisit une devise prise en charge et continue vers un paiement sécurisé.',
            'PayFor est conçu pour financer le service d’un proche sans transformer le payeur en gestionnaire du compte du foyer.',
          ],
        },
        {
          heading: 'Ce que reçoit le foyer à Lomé',
          paragraphs: [
            'Le bénéficiaire reçoit le même service Washed local : une laveuse à domicile, des visites selon la formule choisie et une relation organisée par la plateforme.',
            'Cette page explique le fonctionnement de PayFor ; elle ne remplace pas un lien d’invitation valide.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Puis-je payer sans lien PayFor ?',
          a: 'Le parcours actuel nécessite un lien d’invitation PayFor valide.',
        },
        {
          q: 'Le payeur voit-il les informations privées du foyer ?',
          a: 'Le produit est conçu pour permettre le paiement sans donner accès aux détails privés du foyer.',
        },
        {
          q: 'Le bénéficiaire doit-il être à Lomé ?',
          a: 'Oui : Washed sert Lomé, au Togo.',
        },
      ],
      related: [
        { href: '/lessive-famille-lome', label: 'Voir le service pour les familles' },
        { href: '/prix-laveuse-lome', label: 'Voir les tarifs locaux' },
        { href: '/faq', label: 'Lire la FAQ Washed' },
      ],
      action: {
        href: '/#rejoindre',
        label: 'M’inscrire',
        detail: 'PayFor fonctionne avec une invitation liée à un foyer Washed.',
      },
    },
    en: {
      slug: 'pay-for-family-laundry-togo',
      eyebrow: 'Washed · PayFor',
      title: 'Pay for a family member’s laundry in Togo from abroad',
      intro:
        'PayFor lets someone cover a Washed subscription for a household in Lomé. Payment starts from a secure invitation link; the recipient keeps the local service while the payer does not gain access to private household details.',
      serviceType: 'Pay for a relative’s laundry subscription in Lomé',
      sections: [
        {
          heading: 'How PayFor works',
          paragraphs: [
            'The Washed payment flow uses a PayFor link containing a valid invitation. The payer sees the plan amount, selects a supported currency and continues to secure checkout.',
            'PayFor is designed to fund a loved one’s service without turning the payer into the manager of the recipient’s household account.',
          ],
        },
        {
          heading: 'What the household in Lomé receives',
          paragraphs: [
            'The recipient receives the same local Washed service: an in-home washerwoman, visits according to the selected plan and a relationship organized through the platform.',
            'This page explains PayFor; it does not replace a valid invitation link.',
          ],
        },
      ],
      faqs: [
        {
          q: 'Can I pay without a PayFor link?',
          a: 'The current flow requires a valid PayFor invitation link.',
        },
        {
          q: 'Can the payer see private household information?',
          a: 'The product is designed to enable payment without giving the payer access to private household details.',
        },
        {
          q: 'Does the recipient need to be in Lomé?',
          a: 'Yes: Washed serves Lomé, Togo.',
        },
      ],
      related: [
        { href: '/en/family-laundry-service-lome', label: 'See the family laundry service' },
        { href: '/en/laundry-prices-lome', label: 'See local pricing' },
        { href: '/en/faq', label: 'Read the Washed FAQ' },
      ],
      action: {
        href: '/en#rejoindre',
        label: 'Sign up',
        detail: 'PayFor works through an invitation linked to a Washed household.',
      },
    },
  },
  'washerwoman-jobs': {
    fr: {
      slug: 'travail-laveuse-lome',
      eyebrow: 'Washed · Travail',
      title: 'Travail de laveuse à Lomé avec Washed',
      intro:
        'Washed recrute des laveuses à Lomé pour des missions planifiées auprès de foyers proches. Le modèle prévoit vérification, accompagnement, rémunération définie par la plateforme et relations régulières avec les mêmes foyers lorsque la capacité le permet.',
      sections: [
        {
          heading: 'Comment Washed organise le travail',
          paragraphs: [
            'Avant les premières visites, Washed prévoit une vérification d’identité, des références et un entretien en personne. Les visites sont ensuite regroupées par zone et par créneau afin de limiter les déplacements inutiles.',
            'La rémunération et les éventuels bonus sont définis à l’avance et versés par la plateforme. Washed cherche aussi à faire revenir la même laveuse auprès des mêmes foyers lorsque la capacité le permet.',
          ],
        },
        {
          heading: 'Ce que l’inscription signifie aujourd’hui',
          paragraphs: [
            'La page « Devenir laveuse » permet de laisser son nom, son téléphone, son quartier et, si souhaité, son e-mail afin d’être recontactée.',
            translate('marketing.seo.workers.assignments', 'fr'),
          ],
        },
      ],
      faqs: [
        {
          q: 'Comment postuler comme laveuse Washed ?',
          a: 'Utilisez le formulaire « Devenir laveuse » et indiquez notamment votre nom, téléphone et quartier.',
        },
        {
          q: 'Les missions sont-elles proches du domicile ?',
          a: 'Washed indique qu’il regroupe les visites par zone et par créneau pour réduire les déplacements inutiles.',
        },
        {
          q: 'La rémunération est-elle définie à l’avance ?',
          a: 'Oui. Washed indique que la rémunération et les éventuels bonus sont définis à l’avance et versés par la plateforme.',
        },
      ],
      related: [
        { href: '/devenir-laveuse', label: 'Voir le métier et le formulaire' },
        { href: '/zones', label: translate('marketing.seo.zones.link', 'fr') },
        { href: '/a-propos', label: 'Comprendre la mission de Washed' },
      ],
      action: {
        href: '/devenir-laveuse',
        label: 'Postuler comme laveuse',
        detail: 'Remplissez le formulaire de pré-inscription Washed.',
      },
    },
    en: {
      slug: 'washerwoman-jobs-lome',
      eyebrow: 'Washed · Work',
      title: 'Washerwoman jobs in Lomé with Washed',
      intro:
        'Washed is recruiting washerwomen in Lomé for planned assignments with nearby households. The model includes verification, support, platform-defined compensation and recurring relationships with the same households when capacity allows.',
      sections: [
        {
          heading: 'How Washed organizes the work',
          paragraphs: [
            'Before first visits, Washed describes identity verification, references and an in-person interview. Visits are then grouped by area and time window to reduce unnecessary travel.',
            'Compensation and any applicable bonuses are defined in advance and paid by the platform. Washed also aims to return the same washerwoman to the same households when capacity allows.',
          ],
        },
        {
          heading: 'What applying means today',
          paragraphs: [
            'The “Become a washerwoman” page lets applicants leave their name, phone number, neighbourhood and optional email so the team can follow up.',
            translate('marketing.seo.workers.assignments', 'en'),
          ],
        },
      ],
      faqs: [
        {
          q: 'How do I apply to become a Washed washerwoman?',
          a: 'Use the “Become a washerwoman” form and provide your name, phone number and neighbourhood.',
        },
        {
          q: 'Are assignments close to home?',
          a: 'Washed states that visits are grouped by area and time window to reduce unnecessary travel.',
        },
        {
          q: 'Is compensation defined in advance?',
          a: 'Yes. Washed states that compensation and any applicable bonuses are defined in advance and paid by the platform.',
        },
      ],
      related: [
        { href: '/en/devenir-laveuse', label: 'See the role and application form' },
        { href: '/en/zones', label: translate('marketing.seo.zones.link', 'en') },
        { href: '/en/a-propos', label: 'Understand the Washed mission' },
      ],
      action: {
        href: '/en/devenir-laveuse',
        label: 'Apply as a washerwoman',
        detail: 'Complete the Washed pre-registration form.',
      },
    },
  },
};

interface SeoExpansionPageProps {
  intent: SeoExpansionKey;
  locale: WashedLocale;
  onLocaleChange: (locale: WashedLocale) => void;
}

function pageHref(copy: IntentCopy, locale: WashedLocale): string {
  return locale === 'en' ? `/en/${copy.slug}` : `/${copy.slug}`;
}

export function SeoExpansionPage({ intent, locale, onLocaleChange }: SeoExpansionPageProps) {
  const c = COPY[intent][locale];
  const href = pageHref(c, locale);
  const pageUrl = `${SITE}${href}`;
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: c.title,
      description: c.intro,
      inLanguage: locale,
      isPartOf: { '@id': `${SITE}/#website` },
      about: { '@id': `${SITE}/#organization` },
    },
  ];

  if (c.serviceType) {
    graph.push({
      '@type': 'Service',
      '@id': `${pageUrl}#service`,
      name: c.serviceType,
      serviceType: c.serviceType,
      provider: { '@id': `${SITE}/#organization` },
      areaServed: {
        '@type': 'City',
        name: 'Lomé',
        containedInPlace: { '@type': 'Country', name: 'Togo' },
      },
      url: pageUrl,
      description: c.intro,
    });
  }

  return (
    <PageShell
      locale={locale}
      onLocaleChange={onLocaleChange}
      eyebrow={c.eyebrow}
      title={c.title}
      intro={c.intro}
      cta={false}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }),
        }}
      />

      <div className={styles.article}>
        {c.sections.map((section) => (
          <section key={section.heading} className={styles.section}>
            <h2>{section.heading}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
      </div>

      <section className={styles.faqBlock} aria-labelledby="expansion-faq">
        <h2 id="expansion-faq">
          {locale === 'fr' ? 'Questions fréquentes' : 'Frequently asked questions'}
        </h2>
        <dl className={styles.faqList}>
          {c.faqs.map((item) => (
            <div key={item.q} className={styles.faqItem}>
              <dt>{item.q}</dt>
              <dd>{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <aside
        className={styles.related}
        aria-label={locale === 'fr' ? 'Guides liés' : 'Related guides'}
      >
        <h2>{locale === 'fr' ? 'Guides liés' : 'Related guides'}</h2>
        <div className={styles.relatedGrid}>
          {c.related.map((link) => (
            <a key={link.href} href={link.href} className={styles.relatedCard}>
              <strong>{link.label}</strong>
              <span>{locale === 'fr' ? 'Lire' : 'Read'} →</span>
            </a>
          ))}
          <a href={c.action.href} className={styles.relatedCard}>
            <strong>{c.action.label}</strong>
            <span>{c.action.detail} →</span>
          </a>
        </div>
      </aside>
    </PageShell>
  );
}
